import {Component, Vue, Watch} from 'vue-property-decorator';
import axios from 'axios';
import debounce from 'lodash.debounce';
import {getCommunityEventSummaries} from '@/community-calendar/CommunityCalendarService';
import DropdownButton from '@/components/DropdownButton.vue';
import {CalendarItem} from '@/components/calendar/Calendar';
import {DateTime} from 'luxon';
import {
    CommunityEventSummary,
    CommunityScheduledWeek,
    CommunityScheduledEvent,
    CommunityScheduledDay,
    CommunityEventResponseStatusId
} from '@/community-calendar/CommunityCalendarModel';
import CommunityCalendarEvent from '@/community-calendar/CommunityCalendarEvent.vue';
import { getWeekEnd, getWeekStart, formatWeekTitle, formatDayTitle } from '@/utils/dates';
import CommunityCalendarMonth from '@/community-calendar/CommunityCalendarMonth.vue';

@Component({
    components: {
        DropdownButton,
        CommunityCalendarEvent,
        CommunityCalendarMonth
    }
})
export default class CommunityCalendar extends Vue {
    private loading = false;
    private hasEvents: boolean = false;
    // noinspection JSUnusedLocalSymbols Used in .vue's HTML
    private viewItems = [
        { id: 1, name: 'List View' },
        { id: 2, name: 'Calendar' },
    ];
    private cancelSource = axios.CancelToken.source();
    private searchText = '';
    private data: CommunityScheduledWeek[] = [];
    private monthItems: CalendarItem[] = [];
    private zone = 'America/Chicago';
    private isMonth = true;
    private isMobile = false;

    @Watch('searchText')
    searchTextChanged() {
        this.debounceSearch();
    }

    @Watch('isMonth')
    isMonthChanged() {
        // Only reload when not displaying calendar.
        // The calendar will handle notifying us when it's ready for us to fetch the correct month's data.
        if (!this.isMonth) {
            this.debounceSearch();
        }
    }

    @Watch('isMobile')
    windowWidthChanged() {
        if (this.isMobile) {
            this.isMonth = false;
        }
    }

    mounted() {
        this.checkWindowWidth();
        window.addEventListener('resize', this.checkWindowWidth);
        const observedDiv = document.getElementById('community-calendar-weeks');

        if (observedDiv) {
            // Create a callback function to execute when mutations are observed
            const callback = (mutationsList: MutationRecord[]) => {
                this.focusNextEvent();
            };

            // Create an instance of MutationObserver
            const observer = new MutationObserver(callback);

            // Configuration of the observer:
            const config = { childList: true, subtree: true };

            // Start observing the target node for configured mutations
            observer.observe(observedDiv, config);

        }
    }

    checkWindowWidth() {
        this.isMobile = window.matchMedia('(max-width: 600px)').matches;
    }

    beforeDestroy() {
        window.removeEventListener('resize', this.checkWindowWidth)
    }

    /*
     * Because we want to search as the user types we "debounce" the search.
     * It only fires when the user hasn't typed for at least 300 seconds, but fires at least once every second.
     */
    get debounceSearch(): () => void {
        return debounce(async () => {
            try {
                this.loading = true;
                this.cancelSource.cancel();
                this.cancelSource = axios.CancelToken.source();
                const summaries = await getCommunityEventSummaries(this.getSearchParams(), this.cancelSource.token);
                // defaut status to pending
                this.setData((summaries || []).map(summary => ({...summary, statusId: summary.statusId || CommunityEventResponseStatusId.PENDING})));
                this.hasEvents = this.data.length > 0;
            } catch (err) {
                if (axios.isCancel(err)) {
                    // Do nothing
                } else {
                    (this as any).$notifyError(err, 'Failed to load Community Calendar.');
                }
            } finally {
                this.loading = false; 
               // this.focusNextEvent();
            }
        }, 300, { maxWait: 1000 });
    }

    private focusNextEvent()
    {
         var items = this.data.map(x => {
            return {
                week: x.weekTitle,
                dates:  x.eventsByDay.flatMap(y => y.events.map(z => new Date(z.startDateTime)))
            }
         });

         var currentDate = new Date() ;
         currentDate.setDate(1);

         var filteredEvents = items.filter(x => x.dates.filter(y => y >= currentDate).length > 0);
         if(filteredEvents.length > 0)
         {
            var firstEvent = filteredEvents[0];
            const element = document.getElementById(firstEvent.week);
            if (element) {
                element.tabIndex = 0;
                element.focus();
            }
         }
    }

    private getSearchParams(): { searchText?: string, startDate?: string, endDate?: string } {
        if (this.isMonth) {
            const dates = ((this as any).$refs.calendar as CommunityCalendarMonth).getMonthDates();
            return {
                startDate: dates.start.toISODate(),
                endDate: dates.end.toISODate()
            };
        } else {
            return {
                startDate: DateTime.local().setZone('America/Chicago').toISODate(),
                searchText: this.searchText
            };
        }
    }

    get viewButtonText(): string {
        return this.isMonth ? 'Calendar' : 'List View';
    }

    public async created() {
        this.loading = true;
        await this.debounceSearch();
    }

    private setData(summaries: CommunityEventSummary[]) {
        const { isMonth, zone } = this;
        if (isMonth) {
            this.monthItems = summaries.map((summary) : CalendarItem => ({
                id: summary.id,
                startDateTime: DateTime.fromISO(summary.startDateTime, { zone }),
                text: summary.title,
                data: summary,
                legendKey: summary.statusId === CommunityEventResponseStatusId.PENDING ? 'pending' : 'approved'
            }));
            this.data = [];
        } else {
            this.monthItems = [];

            // build data for UI, order data by date
            this.data = summaries
                .reduce<CommunityScheduledWeek[]>((acc, event): CommunityScheduledWeek[] => {
                    const dateTime = DateTime.fromISO(event.startDateTime, {zone});

                    // create week title. Also use as key for for building UI data array
                    const weekStart = getWeekStart(dateTime);
                    const weekEnd = getWeekEnd(weekStart);
                    const weekTitle = formatWeekTitle(weekStart, weekEnd.get('day'));
                    const dayTitle = formatDayTitle(dateTime);

                    const eventItem = {...event, startTime: dateTime.toFormat('h:mm a ZZZZ')};

                    if (acc.some(e => e.weekTitle === weekTitle)) {
                        // week already added to list, need to add days
                        return [...acc.map(weekSchedule => this.getWeekSchedules(weekSchedule, weekTitle, dayTitle, eventItem))]
                    }

                    // add new week
                    return [...acc, {weekTitle, eventsByDay: [{dayTitle, events: [eventItem]}]}];
                }, []);
        }
    }

    getWeekSchedules(weekSchedule: CommunityScheduledWeek, weekTitle: string, dayTitle: string, eventItem: CommunityScheduledEvent) {
        if (weekSchedule.weekTitle === weekTitle) {
            // only update week with matching title
            if (weekSchedule.eventsByDay.some(e => e.dayTitle === dayTitle)) {
                // day already added to list, need to add event
                return {
                    ...weekSchedule,
                    eventsByDay: [
                        ...weekSchedule.eventsByDay.map(daySchedule => this.getDayEvents(daySchedule, dayTitle, eventItem))
                    ]};
            }

            return {
                ...weekSchedule,
                eventsByDay: [
                    ...weekSchedule.eventsByDay, { dayTitle, events: [eventItem]}
                ]
            };
        }
        return weekSchedule;
    }

    getDayEvents(daySchedule: CommunityScheduledDay, dayTitle: string, eventItem: CommunityScheduledEvent) {
        if (daySchedule.dayTitle === dayTitle) {
            // only update day with matching title
            return {
                ...daySchedule,
                events: [...daySchedule.events, eventItem]
            }
        }
        return daySchedule;
    }

    viewSelected(actionId: number) {
        this.isMonth = actionId === 2;
    }
}
